import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import store from '@/store';
import router from '@/router';

/*
 * The router guard already purges an expired token — but it only runs on
 * navigation, and /game and /game-master are screens people sit on for hours.
 * When the 10h access token died under them nothing navigated, so every request
 * kept sending a dead Bearer, the API answered 401, and each view fell into its
 * own "could not load" branch forever. This closes both halves: a timer that
 * fires at `exp` even while idle, and a 401 interceptor as the backstop.
 */

let watchdog = null;
let expiring = false;

const clearWatchdog = () => {
  if (watchdog !== null) {
    clearTimeout(watchdog);
    watchdog = null;
  }
};

const expiresAt = (token) => {
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    return exp ? exp * 1000 : null;
  } catch (error) {
    /* Undecodable token: treat it as already dead. */
    return 0;
  }
};

export const expireSession = () => {
  if (expiring) return;
  if (!store.getters.getAuthToken) return;

  expiring = true;
  clearWatchdog();

  const current = router.currentRoute.value;
  const wasProtected = current?.matched?.some(record => record.meta.requiresAuth);
  store.dispatch('logout');

  const query = { message: 'SessionExpired' };
  if (wasProtected && current.fullPath) query.redirect = current.fullPath;

  /* A redundant push (already on Login) rejects; that is not a failure here. */
  Promise.resolve(router.push({ name: 'Login', query }))
    .catch(() => {})
    .finally(() => { expiring = false; });
};

const armWatchdog = () => {
  clearWatchdog();
  const deadline = expiresAt(store.getters.getAuthToken);
  if (deadline === null) return;

  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    expireSession();
    return;
  }
  watchdog = setTimeout(expireSession, remaining);
};

export const installSessionGuard = () => {
  axios.interceptors.response.use(
    response => response,
    error => {
      /*
       * Only a token we actually sent can have expired. Without this the login
       * form's own 401 (bad password) would bounce the visitor off the page
       * they are trying to log in from.
       */
      if (error.response?.status === 401 && store.getters.getAuthToken) {
        expireSession();
      }
      return Promise.reject(error);
    },
  );

  /* Re-arm whenever the token changes — login, logout, impersonation. */
  store.subscribe(({ type }) => {
    if (type === 'setToken' || type === 'clearAuth') armWatchdog();
  });

  /* A backgrounded tab can have its timers throttled past the deadline, so
     re-check on the way back in rather than trusting the timer alone. */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') armWatchdog();
  });

  armWatchdog();
};
